import {defineStore} from 'pinia';
import {useEnvironmentStore} from '@internalDevportal/store/EnvironmentStore';
import type {SpecDiffResponse} from '@internalDevportal/types/SpecDiff';
import snackbarService from '@core/services/SnackbarService';
import {ref} from 'vue';
import {AxiosResponse} from 'axios';
import ApiService from '@core/services/ApiService';

export interface Spec {
  id: string;
  productId: string;
  spec: {
    info: {
      title: string;
      version: string;
      description: string;
      contact?: {
        name: string;
      };
    };
    components?: {
      securitySchemes: string;
    };
  };
  createdAt?: string;
  updatedAt: string;
  categoryId: string;
  permissionGroupId: string;
}

export interface NewSpec {
  environmentId: string;
  spec?: any;
  categoryId?: string;
  latest: boolean;
  productId?: string;
  inheritSpec?: boolean;
}

export const useSpecStore = defineStore('SpecStore', () => {
  const api = ApiService.getApiInstance();
  const environmentStore = useEnvironmentStore();

  const specs = ref<Spec[]>([]);
  const latestSpecs = ref<Spec[]>([]);
  const currentSpecs = ref<Spec[]>([]);
  const lastUpdatedSpecs = ref<Spec[]>([]);
  const latestSpecCreated = ref<string>('');
  const latestSpecUpdated = ref<string>('');

  async function deleteSpecById(specId: string) {
    const envId = environmentStore.currentEnvironment?.id;
    if (!envId) {
      return;
    }
    await api.delete(`/specs/${specId}`);
  }

  async function getPublicProductSpecs({productId}: {productId: string}) {
    const filter = JSON.stringify({where: {productId}});
    const response = await api.get(
      `/specs?filter=${encodeURIComponent(filter)}`,
    );
    currentSpecs.value = response.data;
  }

  async function getProductSpecs({productId}: {productId: string}) {
    const envId = environmentStore.currentEnvironment?.id;
    if (!envId) {
      return;
    }
    const response = await api.get(
      `/environments/${envId}/apiproducts/${productId}/specs`,
    );
    currentSpecs.value = response.data;
  }

  async function getSpecs() {
    const envId = environmentStore.currentEnvironment?.id;
    if (!envId) {
      return;
    }
    const response = await api.get(`/environments/${envId}/specs`);
    specs.value = response.data;
  }

  async function getLatestSpecs(limit: number = 5) {
    const envId = environmentStore.currentEnvironment?.id;
    if (!envId) {
      return false;
    }
    const response = await api.get(
      `/environments/${envId}/specs/latest?type=created`,
    );
    const createdAt = response?.data[0]?.createdAt;
    const updatedAt = response?.data[0]?.updatedAt;
    latestSpecCreated.value = createdAt ?? '';
    latestSpecUpdated.value = updatedAt ?? '';
    latestSpecs.value = response.data
      .filter((spec: Spec) => spec.spec)
      .slice(0, limit);
    return latestSpecs.value;
  }

  async function getLastUpdatedSpecs(limit: number = 5) {
    const envId = environmentStore.currentEnvironment?.id;
    if (!envId) {
      return false;
    }
    const response = await api.get(
      `/environments/${envId}/specs/latest?type=updated`,
    );
    lastUpdatedSpecs.value = response.data
      .filter((spec: Spec) => spec.spec)
      .slice(0, limit);
  }

  async function getSpecDiff({
    fromSpecId,
    toSpecId,
  }: {
    fromSpecId: string;
    toSpecId: string;
  }): Promise<SpecDiffResponse> {
    const response = await api.get<SpecDiffResponse>(
      `/changelogs?fromSpecId=${encodeURIComponent(fromSpecId)}&toSpecId=${encodeURIComponent(toSpecId)}`,
    );
    return response.data;
  }

  async function getSpecById({specId}: {specId: string}): Promise<any> {
    return api
      .get(`/specs/${specId}`, {responseType: 'json'})
      .then((response: AxiosResponse) => response.data.spec);
  }

  async function uploadSpec(spec: NewSpec) {
    const response = await api.post('/specs', spec);
    if (response.status === 200 || response.status === 201) {
      snackbarService.showHappySnackbar('Spec uploaded successfully');
    }
    await getSpecs();
  }

  async function downloadSpec({specId}: {specId: string}) {
    const spec = await getSpecById({specId});
    const fileURL = globalThis.URL.createObjectURL(
      new Blob([JSON.stringify(spec, null, 4)]),
    );
    const fileLink = document.createElement('a');

    fileLink.href = fileURL;
    fileLink.setAttribute('download', 'swagger.json');
    document.body.append(fileLink);

    fileLink.click();

    setTimeout(() => {
      globalThis.URL.revokeObjectURL(fileURL);
      fileLink.remove();
    }, 100);
  }

  function $reset() {
    specs.value = [];
    latestSpecs.value = [];
    currentSpecs.value = [];
    lastUpdatedSpecs.value = [];
    latestSpecCreated.value = '';
    latestSpecUpdated.value = '';
  }

  return {
    specs,
    latestSpecs,
    currentSpecs,
    lastUpdatedSpecs,
    latestSpecCreated,
    latestSpecUpdated,
    deleteSpecById,
    getPublicProductSpecs,
    getProductSpecs,
    getSpecs,
    getLatestSpecs,
    getLastUpdatedSpecs,
    getSpecDiff,
    getSpecById,
    uploadSpec,
    downloadSpec,
    $reset,
  };
});
